import { Play, RotateCcw } from 'lucide-react';
import { useEffect, useState } from 'react';

import { VideoLoader } from '@/components/ui/video-loader';
import { useTranslations } from '@/hooks/use-translations';
import { cn } from '@/lib/utils';

import defaultConfig from '../config/speaker-video-config.json';
import { useSpeakerVideo } from '../hooks/useSpeakerVideo';
import { SpeakerVideoProps, SpeakerVideoConfig } from '../types';

/**
 * SpeakerVideo Component
 *
 * Displays a floating circular video with progress bar.
 * Used to show expert or trainer interventions.
 *
 * Features:
 * - Circular video with scale in/out animation
 * - Circular progress bar around the video
 * - Auto-play and automatic end-of-video management
 * - Responsive (small on mobile, large on desktop)
 * - Configurable position (screen corners)
 *
 * Behavior:
 * - Appears with scale-in animation when videoUrl is provided
 * - Automatically starts playback
 * - Calls onVideoComplete when video ends
 * - Disappears with scale-out animation after ending
 * - Loader displayed during video loading
 *
 * @example
 * ```tsx
 * <SpeakerVideo
 *   videoUrl="/videos/expert-intro.mp4"
 *   onVideoEnd={() => console.log('Video finished')}
 *   onVideoComplete={() => setIsComplete(true)}
 *   position="bottom-right"
 *   showProgress={true}
 * />
 * ```
 */
export const SpeakerVideo = ({
  videoUrl,
  onVideoEnd,
  onVideoComplete,
  onReplay,
  position = 'bottom-right',
  showProgress = true,
  autoPlay = true,
  allowPause = true,
}: SpeakerVideoProps) => {
  const { t } = useTranslations();
  const config = defaultConfig as SpeakerVideoConfig;
  const [lastPlayedVideoUrl, setLastPlayedVideoUrl] = useState<string | null>(null);

  const {
    videoRef,
    currentProgress,
    isVisible,
    isLoading,
    isComplete,
    isPlaying,
    togglePlayPause,
  } = useSpeakerVideo(videoUrl, onVideoEnd, config);

  // Notify parent when video ends and save last played video
  useEffect(() => {
    if (isComplete && onVideoComplete) {
      onVideoComplete();
      // Save last played video URL for replay
      if (videoUrl) {
        setLastPlayedVideoUrl(videoUrl);
      }
    }
  }, [isComplete, onVideoComplete, videoUrl]);

  const handleReplayVideo = () => {
    if (lastPlayedVideoUrl && onReplay) {
      onReplay(lastPlayedVideoUrl);
    }
  };

  // Show replay button if video finished and no current video
  if (!videoUrl && lastPlayedVideoUrl) {
    return (
      <div
        className={cn(
          'relative flex items-center justify-center',
          position === 'top-left' && 'justify-start',
          position === 'top-right' && 'justify-end'
        )}
        style={{ zIndex: config.zIndex }}
      >
        <button
          onClick={handleReplayVideo}
          className="group flex h-12 w-12 animate-fade-in items-center justify-center rounded-full bg-primary text-white shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl md:h-16 md:w-16"
          aria-label={t('common.aria.replayVideo')}
        >
          <RotateCcw className="h-6 w-6 transition-transform duration-500 group-hover:rotate-180 md:h-8 md:w-8" />
        </button>
      </div>
    );
  }

  // Don't render component if no video
  if (!videoUrl) return null;

  // Calculate values for circular progress bar
  const mobileCircumference = 2 * Math.PI * config.size.mobile.progressRadius;
  const desktopCircumference = 2 * Math.PI * config.size.desktop.progressRadius;
  const mobileStrokeDashoffset =
    mobileCircumference - (currentProgress / 100) * mobileCircumference;
  const desktopStrokeDashoffset =
    desktopCircumference - (currentProgress / 100) * desktopCircumference;

  // Compute aria-label value
  let ariaLabel: string | undefined;
  if (allowPause) {
    ariaLabel = isPlaying ? 'Pause video' : 'Play video';
  }

  return (
    <div
      className={cn(
        'relative flex items-center justify-center',
        position === 'top-left' && 'justify-start',
        position === 'top-right' && 'justify-end'
      )}
      style={{ zIndex: config.zIndex }}
    >
      <div
        className={`group relative transition-all duration-${
          config.animation.scaleInDuration
        } ease-out ${isVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`}
      >
        {/* Circular progress bar - Mobile */}
        {showProgress && (
          <svg
            className="pointer-events-none absolute -left-2 -top-2 z-10 h-20 w-20 -rotate-90 md:hidden"
            viewBox="0 0 80 80"
          >
            {/* Background circle */}
            <circle
              cx="40"
              cy="40"
              r={config.size.mobile.progressRadius}
              fill="none"
              stroke={config.progressBar.backgroundColor}
              strokeWidth={config.progressBar.strokeWidth.mobile}
            />
            {/* Progress circle */}
            <circle
              cx="40"
              cy="40"
              r={config.size.mobile.progressRadius}
              fill="none"
              stroke={config.progressBar.progressColor}
              strokeWidth={config.progressBar.strokeWidth.mobile}
              strokeDasharray={mobileCircumference}
              strokeDashoffset={mobileStrokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          </svg>
        )}

        {/* Circular progress bar - Desktop */}
        {showProgress && (
          <svg
            className="pointer-events-none absolute -left-2 -top-2 z-10 hidden h-36 w-36 -rotate-90 md:block"
            viewBox="0 0 144 144"
          >
            {/* Background circle */}
            <circle
              cx="72"
              cy="72"
              r={config.size.desktop.progressRadius}
              fill="none"
              stroke={config.progressBar.backgroundColor}
              strokeWidth={config.progressBar.strokeWidth.desktop}
            />
            {/* Progress circle */}
            <circle
              cx="72"
              cy="72"
              r={config.size.desktop.progressRadius}
              fill="none"
              stroke={config.progressBar.progressColor}
              strokeWidth={config.progressBar.strokeWidth.desktop}
              strokeDasharray={desktopCircumference}
              strokeDashoffset={desktopStrokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          </svg>
        )}

        {/* Circular video */}
        <div
          className={cn(
            'relative h-16 w-16 overflow-hidden rounded-full bg-white shadow-xl md:h-32 md:w-32 md:shadow-2xl',
            allowPause && 'cursor-pointer'
          )}
          onClick={allowPause ? togglePlayPause : undefined}
          onKeyDown={
            allowPause
              ? (e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    togglePlayPause();
                  }
                }
              : undefined
          }
          role={allowPause ? 'button' : undefined}
          aria-label={ariaLabel}
          tabIndex={allowPause ? 0 : undefined}
        >
          {/* Loader during loading */}
          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <VideoLoader size="small" />
            </div>
          )}

          {/* Play/Pause overlay indicator */}
          {allowPause && !isPlaying && !isLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/20 transition-opacity">
              <Play className="h-6 w-6 text-white drop-shadow-lg md:h-12 md:w-12" fill="white" />
            </div>
          )}

          {/* Video element */}
          <video
            ref={videoRef}
            src={videoUrl}
            className={cn(
              'h-full w-full object-cover transition-opacity duration-500 ease-in-out',
              isLoading ? 'opacity-0' : 'opacity-100'
            )}
            playsInline
            autoPlay={autoPlay}
            disableRemotePlayback
          />
        </div>
      </div>
    </div>
  );
};
